import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Download, Check, X } from "lucide-react";
import portalApi from "@/lib/portalApi";
import { formatApiError } from "@/lib/api";
import { Pill, QUOTE_STATUS, fmtDate, fmtMoney } from "@/components/portal/portalUi";

const OFFER_STATUS = { envoye: ["En attente de votre décision", "amber"], accepte: ["Accepté", "green"], refuse: ["Refusé", "slate"] };

function Offer({ q, onDecided }) {
  const [refusing, setRefusing] = useState(false);
  const [reason, setReason] = useState("");
  const o = q.offer;
  const expired = o.valid_until && o.valid_until < new Date().toISOString().slice(0, 10);
  const decide = async (decision) => {
    if (decision === "accept" && !window.confirm(`Confirmer l'acceptation du devis de ${fmtMoney(o.amount, o.currency)} ?`)) return;
    try { await portalApi.post(`/portal/quotes/${q.id}/decision`, { decision, reason }); toast.success(decision === "accept" ? "Devis accepté, merci !" : "Votre réponse a été transmise"); onDecided(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="mt-5 p-4 sm:p-5 rounded-xl bg-[#0F2A6B]/[0.03] border border-[#0F2A6B]/10" data-testid={`portal-offer-${q.id}`}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">Montant proposé</div>
          <div className="text-2xl font-bold text-[#0F2A6B] font-head" data-testid={`portal-offer-amount-${q.id}`}>{fmtMoney(o.amount, o.currency)}</div>
          {o.valid_until && <div className={`text-xs mt-1 ${expired && q.offer_status === "envoye" ? "text-rose-600" : "text-slate-500"}`}>Valable jusqu'au {fmtDate(o.valid_until)}{expired && q.offer_status === "envoye" && " — expiré"}</div>}
        </div>
        <Pill map={OFFER_STATUS} value={q.offer_status} testid={`portal-offer-status-${q.id}`} />
      </div>
      {o.conditions && <p className="mt-3 text-sm text-slate-600 whitespace-pre-wrap">{o.conditions}</p>}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {o.attachment?.url && <a href={o.attachment.url} target="_blank" rel="noopener noreferrer" data-testid={`portal-offer-pdf-${q.id}`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-200 bg-white text-sm font-semibold text-[#0F2A6B] hover:border-[#0F2A6B]/40"><Download className="w-4 h-4" /> Télécharger le devis</a>}
        {q.offer_status === "envoye" && !expired && !refusing && (<>
          <button onClick={() => decide("accept")} data-testid={`portal-offer-accept-${q.id}`} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-[#4CB944] text-white text-sm font-semibold hover:bg-[#3AA033] hover:shadow-lg hover:shadow-[#4CB944]/25 transition-[background-color,box-shadow]"><Check className="w-4 h-4" /> Accepter</button>
          <button onClick={() => setRefusing(true)} data-testid={`portal-offer-refuse-${q.id}`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-slate-600 hover:bg-slate-100"><X className="w-4 h-4" /> Refuser</button>
        </>)}
      </div>
      {refusing && (
        <div className="mt-4 space-y-2">
          <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motif (facultatif)" data-testid={`portal-offer-refuse-reason-${q.id}`} className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 text-sm focus:border-[#0F2A6B] focus:outline-none" />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setRefusing(false)} className="px-4 py-2 rounded-full text-sm text-slate-600 hover:bg-slate-100">Annuler</button>
            <button onClick={() => decide("refuse")} data-testid={`portal-offer-refuse-confirm-${q.id}`} className="px-4 py-2 rounded-full bg-rose-600 text-white text-sm font-semibold">Confirmer le refus</button>
          </div>
        </div>
      )}
      {q.client_decision?.reason && <p className="mt-3 text-xs text-slate-500">Votre motif : « {q.client_decision.reason} »</p>}
    </div>
  );
}

export function QuotesTab() {
  const [items, setItems] = useState(null);
  const load = () => portalApi.get("/portal/quotes").then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  if (!items) return <div className="py-16 text-center text-slate-400">Chargement…</div>;
  if (!items.length) return <Empty text="Aucune demande de devis pour le moment." />;
  return (
    <div className="grid gap-4" data-testid="portal-quotes-list">
      {items.map((q) => (
        <article key={q.id} data-testid={`portal-quote-${q.id}`} className="p-5 sm:p-6 rounded-2xl bg-white border border-slate-200 hover:border-[#0F2A6B]/30 hover:shadow-md transition-[border-color,box-shadow]">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-xs font-mono uppercase text-slate-400">Demande du {fmtDate(q.created_at)}</div>
              <h3 className="mt-1 text-lg font-semibold text-[#0F2A6B] font-head">{q.solution_service || q.type_besoin || "Projet"}</h3>
            </div>
            {!q.offer_status && <Pill map={QUOTE_STATUS} value={q.status} />}
          </div>
          {q.description && <p className="mt-3 text-sm text-slate-600 line-clamp-3">{q.description}</p>}
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
            {q.type_besoin && <span>Besoin : <b className="text-slate-700">{q.type_besoin}</b></span>}
            {q.budget && <span>Budget : <b className="text-slate-700">{q.budget}</b></span>}
            {q.delai && <span>Délai : <b className="text-slate-700">{q.delai}</b></span>}
          </div>
          {q.offer && <Offer q={q} onDecided={load} />}
        </article>
      ))}
    </div>
  );
}

export function Empty({ text }) {
  return (
    <div className="py-16 text-center rounded-2xl border border-dashed border-slate-300 bg-white">
      <FileText className="w-8 h-8 mx-auto text-slate-300" />
      <p className="mt-3 text-sm text-slate-500">{text}</p>
    </div>
  );
}
